<template>
  <div ref="wrapperRef" class="m-tasks-wrapper">
    <div class="m-tasks-container">
      <!-- Task groups/steps -->
      <div 
        v-for="(task, index) in tasks"
        :key="task.id || index"
        class="task-item"
        :class="getTaskStatusClass(task)"
      >
        <div class="task-header">
          <div class="task-number">#{{ index + 1 }}</div>
          <div class="task-info">
            <div class="task-title">
              {{ task.subject || task.title || 'Задача' }}
            </div>
            <div class="task-meta">
              <span v-if="task.executor" class="task-executor">
                <i class="pi pi-user"></i>
                {{ task.executor }}
              </span>
              <span v-if="task.expireDate" class="task-deadline">
                <i class="pi pi-calendar"></i>
                {{ formatDate(task.expireDate) }}
              </span>
              <span v-if="task.status" class="task-status">
                <i class="pi pi-circle-fill"></i>
                {{ task.status }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="task.comment" class="task-comment">
          {{ task.comment }}
        </div>

        <div v-if="editable && !readonly" class="task-actions">
          <Button
            icon="pi pi-check"
            label="Выполнить"
            class="p-button-sm p-button-success"
            @click="handleTaskComplete(task, index)"
          />
          <Button
            icon="pi pi-times"
            label="Отменить"
            class="p-button-sm p-button-danger"
            @click="handleTaskCancel(task, index)"
          />
          <Button
            icon="pi pi-pencil"
            label="Изменить"
            class="p-button-sm p-button-text"
            @click="handleTaskEdit(task, index)"
          />
        </div>
      </div>

      <!-- Add new task button -->
      <div v-if="editable && !readonly && canAddTasks" class="task-add">
        <Button
          icon="pi pi-plus"
          label="Добавить задачу"
          class="p-button-outlined"
          @click="handleAddTask"
        />
      </div>

      <!-- Empty state -->
      <div v-if="tasks.length === 0" class="tasks-empty">
        <i class="pi pi-list"></i>
        <p>Нет задач</p>
      </div>
    </div>

    <div v-if="hint" class="m-tasks-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface Task {
  id?: string | number
  subject?: string
  title?: string
  executor?: string
  expireDate?: Date | string
  status?: string
  comment?: string
  isControl?: boolean
  isExecuted?: boolean
}

interface Props {
  modelValue?: Task[]
  editable?: boolean
  readonly?: boolean
  canAddTasks?: boolean
  hint?: string
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  editable: true,
  readonly: false,
  canAddTasks: true,
  hint: '',
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [tasks: Task[]]
  'change': [tasks: Task[]]
  'task-complete': [task: Task]
  'task-cancel': [task: Task]
  'task-edit': [task: Task]
  'task-add': []
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)

const tasks = computed(() => props.modelValue || [])

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const getTaskStatusClass = (task: Task) => ({
  'task-completed': task.isExecuted,
  'task-control': task.isControl,
  'task-overdue': isOverdue(task)
})

const isOverdue = (task: Task): boolean => {
  if (!task.expireDate || task.isExecuted) return false
  const expireDate = new Date(task.expireDate)
  return expireDate < new Date()
}

const formatDate = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleDateString('ru-RU')
}

const handleTaskComplete = (task: Task, index: number) => {
  const updatedTasks = [...tasks.value]
  updatedTasks[index] = { ...task, isExecuted: true, status: 'Выполнена' }
  emit('update:modelValue', updatedTasks)
  emit('change', updatedTasks)
  emit('task-complete', task)
}

const handleTaskCancel = (task: Task, index: number) => {
  const updatedTasks = [...tasks.value]
  updatedTasks.splice(index, 1)
  emit('update:modelValue', updatedTasks)
  emit('change', updatedTasks)
  emit('task-cancel', task)
}

const handleTaskEdit = (task: Task, index: number) => {
  emit('task-edit', task)
}

const handleAddTask = () => {
  emit('task-add')
}
</script>

<style scoped lang="scss">
.m-tasks-wrapper {
  width: 100%;
}

.m-tasks-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.task-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid var(--primary-color, #1e3a6e);
  border-radius: 4px;
  padding: 1rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.task-completed {
    border-left-color: #10b981;
    background: #f0fdf4;
  }

  &.task-control {
    border-left-color: #f59e0b;
  }

  &.task-overdue {
    border-left-color: #ef4444;
  }
}

.task-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.task-number {
  background: var(--primary-color, #1e3a6e);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.task-info {
  flex: 1;
}

.task-title {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.task-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;

  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  i {
    font-size: 0.75rem;
  }
}

.task-executor {
  color: var(--primary-color, #1e3a6e);
}

.task-deadline {
  color: #f59e0b;
}

.task-status {
  color: #10b981;
}

.task-comment {
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0.5rem 0;
}

.task-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.task-add {
  padding: 1rem;
  border: 2px dashed #d1d5db;
  border-radius: 4px;
  display: flex;
  justify-content: center;

  &:hover {
    border-color: var(--primary-color, #1e3a6e);
    background: color-mix(in srgb, var(--primary-color, #1e3a6e) 6%, white);
  }
}

.tasks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
  }
}

.m-tasks-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f3f4f6;
  font-weight: 600;

  .group-name {
    color: #1f2937;
  }

  .group-count {
    color: #6b7280;
    font-size: 0.875rem;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
}
</style>

